import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// Define the context type
type LanguageContextType = {
  language: string;
  setLanguage: (lang: string) => void;
  availableLanguages: { code: string; name: string }[];
  getDisplayLanguage: () => string;
};

// Create the context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  availableLanguages: [
    { code: 'en', name: 'English' },
    { code: 'zh', name: '中文' }
  ],
  getDisplayLanguage: () => 'en'
});

// Create a provider component
export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [language, setLanguageState] = useState(i18n.language || 'en');
  
  const availableLanguages = [
    { code: 'en', name: 'English' },
    { code: 'zh', name: '中文' }
  ];

  // Function to change language
  const setLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setLanguageState(lang);
    // Save to localStorage
    localStorage.setItem('language', lang);
    // Update HTML lang attribute for SEO
    document.documentElement.lang = lang;
  };

  // Function to get display language (just the main part like 'EN' instead of 'EN-AU')
  const getDisplayLanguage = () => {
    // Get the main language code (e.g., 'en' from 'en-AU')
    return language.split('-')[0];
  };

  // Set initial HTML lang attribute on mount
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, availableLanguages, getDisplayLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = () => useContext(LanguageContext);
